import React from 'react';
import { Tooltip as MuiTooltip, styled, tooltipClasses } from '@mui/material';
import type { TooltipProps as MuiTooltipProps } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Colors } from 'library';
import { Icon } from './Icon';
import { Stack } from './Stack';
import { Text } from './Text';

type TooltipVariant = 'dark' | 'light';

interface TooltipProps {
    body: React.ReactNode;
    children?: React.ReactNode;
    icon?: SvgIconComponent | IconDefinition;
    heading?: React.ReactNode;
    placement?: MuiTooltipProps['placement'];
    arrow?: boolean;
    variant?: TooltipVariant;
    className?: string;
}

export function Tooltip({
    icon,
    children,
    body,
    heading,
    placement = 'top',
    arrow = true,
    variant = 'dark',
    className,
}: TooltipProps) {
    return (
        <StyledTooltip
            placement={placement}
            arrow={arrow}
            $variant={variant}
            className={className}
            title={
                <Stack gap="sm">
                    {heading && (
                        <Text weight="semibold" tone="overlay">
                            {heading}
                        </Text>
                    )}
                    <div>{body}</div>
                </Stack>
            }
        >
            <Trigger>
                {children ??
                    (icon && <Icon icon={icon} size="sm" bgColor="transparent" iconColor={Colors.textSecondary} />)}
            </Trigger>
        </StyledTooltip>
    );
}

interface TextTooltipProps {
    title: React.ReactNode;
    children: React.ReactElement;
    placement?: MuiTooltipProps['placement'];
    arrow?: boolean;
    variant?: TooltipVariant;
    className?: string;
}

export function TextTooltip({
    title,
    children,
    placement = 'top',
    arrow = true,
    variant = 'dark',
    className,
}: TextTooltipProps) {
    return (
        <StyledTooltip title={title} placement={placement} arrow={arrow} $variant={variant} className={className}>
            {children}
        </StyledTooltip>
    );
}

const Trigger = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',
    lineHeight: 0,
});

const StyledTooltip = styled(
    ({ className, ...props }: MuiTooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />,
    { shouldForwardProp: (p) => p !== '$variant' },
)<{ $variant?: TooltipVariant }>(({ $variant = 'dark' }) => {
    const light = $variant === 'light';
    return {
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: light ? Colors.white : Colors.primary,
            color: light ? Colors.primary : Colors.white,
            fontSize: 12,
            lineHeight: 1.5,
            padding: light ? 16 : '8px 10px',
            borderRadius: 8,
            maxWidth: light ? 400 : 260,
            boxShadow: light ? '0 4px 20px rgba(0, 0, 0, 0.15)' : undefined,
            border: light ? `1px solid ${Colors.border}` : undefined,
        },
        [`& .${tooltipClasses.arrow}`]: light
            ? { color: Colors.white, '&::before': { border: `1px solid ${Colors.border}` } }
            : { color: Colors.primary },
    };
});
